/**
 * RevokeIntakeLinkDialog — confirmation simple de révocation (Chunk 9.N.3.A).
 *
 * Décision PO 9.N.3.A : second click sur bouton « Révoquer » destructive suffit
 * (pas de checkbox « Je comprends »). UX cohérente — la révocation est non-
 * destructive côté DB (soft, audit conservé) et le broker peut recréer un
 * nouveau lien en quelques secondes via le dialog Create.
 *
 * Pattern dialog maison parité [CreateIntakeLinkDialog] — `role="dialog"` +
 * `aria-modal` + Escape + backdrop + focus initial sur Cancel pour éviter
 * un clic réflexe sur Revoke (parité GitHub destructive ops).
 *
 * Erreurs : message bilingue inline + retry possible. La modale ne se ferme
 * que sur succès ou click explicite Cancel.
 */

import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import {
  getApiErrorMessage,
  normalizeLang,
} from "../../../lib/apiErrorMessage";
import { useRevokeIntakeLink } from "../../../lib/queries/intakeLinks";

type Props = {
  open: boolean;
  intakeLinkId: string | null;
  /** Titre du projet associé (pour contexte UX). `null` si projet soft-deleté. */
  projectTitle: string | null;
  onClose: () => void;
  onRevoked: () => void;
};

export function RevokeIntakeLinkDialog({
  open,
  intakeLinkId,
  projectTitle,
  onClose,
  onRevoked,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const revoke = useRevokeIntakeLink();
  const { reset: resetMutation } = revoke;

  useEffect(() => {
    if (open) {
      // Reset uniquement à la (ré)ouverture. Ne pas inclure `revoke` dans les
      // dep — l'objet retourné par useMutation change d'identité à chaque
      // render → boucle infinie qui clear `error` aussitôt qu'il apparaît.
      // Référence à la fonction `reset` (stable) seulement.
      resetMutation();
      // Focus initial sur Cancel (anti-clic-réflexe sur Revoke).
      queueMicrotask(() => cancelBtnRef.current?.focus());
    }
  }, [open, resetMutation]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !revoke.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, revoke.isPending]);

  if (!open || !intakeLinkId) return null;

  const onConfirm = () => {
    revoke.mutate(intakeLinkId, {
      onSuccess: () => onRevoked(),
    });
  };

  const apiErrorMsg =
    revoke.error instanceof ApiException
      ? getApiErrorMessage(revoke.error, lang) || t("errors.generic")
      : revoke.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!revoke.isPending) onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("intake_links.revoke.title")}
          </h2>
        </header>

        <div className="space-y-3 px-5 py-5">
          <p className="text-sm text-slate-700">
            {projectTitle
              ? t("intake_links.revoke.body_with_project", { project: projectTitle })
              : t("intake_links.revoke.body_no_project")}
          </p>
          <p className="text-sm text-slate-500">
            {t("intake_links.revoke.warning")}
          </p>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={revoke.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={revoke.isPending}
            aria-busy={revoke.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-60 touch-manipulation"
          >
            {revoke.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {revoke.isPending
              ? t("intake_links.revoke.submitting")
              : t("intake_links.revoke.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
